// The packing flow's instructions in the packer's own language (Fernando, 2026-09-28: "an
// instruction that we can translate to Estonian or Russian besides EN — per user"). The
// language is a per-person preference (Settings → My profile); on a tablet it is the language
// of the PERSON chosen in "Who is picking?", not of the device login.
// ET and RU were written by Claude and are to be checked by Fernando.
import { CARRIER_NAME } from './carriers';

export type Lang = 'en' | 'et' | 'ru';
export const LANGS: { key: Lang; label: string }[] = [
  { key: 'en', label: 'English' }, { key: 'et', label: 'Eesti' }, { key: 'ru', label: 'Русский' },
];

interface Texts {
  missingTitle: (order: string, tab: string) => string;
  missingSteps: string[];
  noLabelTitle: string;
  noLabelSteps: string[];
  noLabelMoved: (order: string) => string;
  // too big for a bag (Fernando 1.11): pressed at the bag moment, the packer picks a free grey box, a manager splits
  tooBigBtn: string;
  tooBigTitle: (order: string) => string;
  tooBigPick: string;                       // step 1, with the three box buttons
  tooBigSteps: string[];                    // steps 2 and 3
  tooBigMoved: (order: string, box: number) => string;
  tooBigMoveFailed: string;
  noLabelMoveFailed: string;
  retry: string;
  done: string;
  cartLabel: string;
  cartLine: (cart: string) => string;
  scanNext: string;
  // the Process confirmation (2026-10-05): the empty-boxes check, and a round not fully packed
  boxCheck: string;
  boxCheckHint: string;
  roundHeldTitle: string;
  roundHeldBody: string;
  cancel: string;
  // the picker rule (2026-10-08): a shelf under inventory count
  inventoryCounting: string;
  inventoryCountingLong: string;
  /** "Just look" (26.10): the line waits for a NOS supplier delivery — n of the row's pieces */
  nosWaiting: (n: number, of: number) => string;
  nosWaitingLong: (n: number, of: number) => string;
  // the Process dialog's chrome (review 5.10: it was English for ET/RU packers)
  process: string;
  ok: string;
  roundWord: string;
  stillToPack: string;
  labelNotScanned: string;
  labelNotScannedHint: string;
  notProcessedNow: string;
  processedOutside: (n: number) => string;
  waitHint: string;
  forceHint: string;
  forceButton: string;
  // new people (Fernando 21.10, teaching Shaw): the picking screen's headings, the packing table, "scan each piece",
  // "now scan the delivery note"
  colShelf: string;
  colQty: string;
  colItem: string;
  pcs: string;
  inStock: (n: number) => string;
  colLocation: string;
  colProduct: string;
  colScanned: string;
  packBtn: string;
  noteRow: string;
  noteRowHint: string;
  noteScanNow: string;
  allScannedNote: string;
  multiTitle: string;
  multiBody: string;
  multiOk: string;
  // the places taken at the station — Ä, Ö, CAMPAIGN (Fernando 21.10 / 5.11): taken while packing, never picked with
  // the cart; the title names the places of THIS order ('Ä and CAMPAIGN')
  specialsTitle: (places: string[]) => string;
  placeA: string;
  placeO: string;
  specialsBody: string;
  specialsOk: string;
  dontRemind: string;
  atStation: string;
  // the tablet's Can't find, shown at packing (21.10)
  shortTitle: string;
  shortBody: string;
  shortOk: string;
  shortBanner: string;
  shortBannerHint: string;
  // Can't find on the item's own line (21.10)
  cantFindBtn: string;
  howManyMissing: string;
  cantFindConfirm: string;
  hardToFind: string;
  missingMark: (n: number) => string;
  undo: string;
  markRestHint: string;
  // the parcel steps before the label (21.10: EE19537 left without its delivery note)
  parcelSteps: string[];
  noteTick: string;
  noteTickFirst: string;
  // 1.11: a parcel label scanned at "Scan order barcode" belongs to a packed order whose gate was lost — straight there
  labelGateResume: (order: string) => string;
  noShelf: string;
  // 2.11: a manager's fix at a station — the order is processed at its label scan
  fixProcessing: string;
  fixProcessed: (order: string) => string;
  fixNotProcessed: (order: string, why: string) => string;
  fixNewNote: string;
}

/** 'Ä' · 'Ä and Ö' · 'Ä, CAMPAIGN and Ö' — the places named in the packer's language */
function andList(items: string[], and: string): string {
  return items.length < 2 ? (items[0] || '') : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

const T: Record<Lang, Texts> = {
  en: {
    missingTitle: (o, t) => `Item missing — ${o} moved to ${t}`,
    missingSteps: [
      'Put all the items of this order in the bag',
      'Leave the bag open',
      'Write the order number on the bag',
      'Put the delivery note and the label inside the bag',
      'Put the open bag in the grey box',
    ],
    noLabelTitle: 'All items verified ✓ — no shipping label yet',
    noLabelSteps: [
      'Seal the bag',
      'Write the order number on the bag with the permanent marker',
    ],
    noLabelMoved: o => `${o} moved to Label issue — scan the next delivery note`,
    tooBigBtn: 'Too big for a bag — needs a manager',
    tooBigTitle: o => `${o} is too big for a bag`,
    tooBigPick: 'Take an empty grey box with Too big 1, 2 or 3 written on it — press its number:',
    tooBigSteps: [
      'Put all the items, the delivery note and the label inside the box',
      'Put the grey box on the Too big shelf',
    ],
    tooBigMoved: (o, b) => `${o} is in grey box Too big ${b} and moved to Too Big — a manager takes it from there`,
    tooBigMoveFailed: 'Could not move the order — press the box number again',
    noLabelMoveFailed: 'The order could not be moved to Label issue — try again, or move it from the Orders page',
    retry: 'Try again',
    done: 'Done — next order',
    cartLabel: 'Cart',
    cartLine: c => `Put the parcel in the ${c} cart`,
    scanNext: 'Scan the next delivery note',
    boxCheck: 'I checked the boxes: no item and no delivery note is left over',
    boxCheckHint: 'Look into every box and over the packing table before you confirm — a forgotten item is a customer complaint.',
    roundHeldTitle: "You can't process the orders of this round yet",
    roundHeldBody: 'Not every order of the round is packed — delivery note, items, note again, label. Finish packing the whole round first.',
    cancel: 'Cancel',
    inventoryCounting: 'Inventory counting here — leave it, inventory brings it',
    inventoryCountingLong: 'This shelf is being counted right now. Do not take the piece; the inventory person brings it to the packer.',
    nosWaiting: (n, of) => n < of ? `Waiting for NOS · ${n} of ${of}` : 'Waiting for NOS',
    nosWaitingLong: (n, of) => `${n} of ${of} pcs wait for the supplier delivery (NOS) — they cannot be picked until it arrives.`,
    process: 'Process',
    ok: 'OK',
    roundWord: 'Round',
    stillToPack: 'Still to pack',
    labelNotScanned: 'Label not scanned',
    labelNotScannedHint: "Scan the order's delivery note again in the packing session and finish it — label included.",
    notProcessedNow: 'Not processed now',
    processedOutside: n => `${n} order${n === 1 ? '' : 's'} outside this round ${n === 1 ? 'was' : 'were'} processed.`,
    waitHint: 'Just packed the last one? Wait a moment and press Process again — the scans arrive within seconds.',
    forceHint: 'Manager / admin: process these orders anyway. Say why — it is written on each order.',
    forceButton: 'Process anyway',
    colShelf: 'Shelf',
    colQty: 'Qty',
    colItem: 'Item (in stock)',
    pcs: 'pcs',
    inStock: n => `${n} in stock`,
    colLocation: 'Location',
    colProduct: 'Product',
    colScanned: 'Scanned',
    packBtn: 'Pack',
    noteRow: 'Delivery note',
    noteRowHint: 'scan it last',
    noteScanNow: 'Scan now',
    allScannedNote: 'All items scanned ✓ — now scan the DELIVERY NOTE',
    multiTitle: 'More than one piece',
    multiBody: 'This order needs more than one piece of the same item. Scan EVERY piece on its own — each piece must beep.',
    multiOk: 'I understand — I scan each piece',
    specialsTitle: p => `${andList(p, 'and')} ${p.length > 1 ? 'are' : 'is'} right here at the station`,
    placeA: 'Ä: the shelf next to this station',
    placeO: 'Ö: this table (socks)',
    specialsBody: 'Take it now, while you pack — nobody picks these with the cart.',
    specialsOk: 'OK, got it',
    dontRemind: "Don't remind me anymore",
    atStation: 'at the station',
    shortTitle: 'The picker could not find this',
    shortBody: "If you have it now, scan it as usual. If it is still missing, press Can't find on its line: the order then goes to Can't find with its note, pieces and label.",
    shortOk: 'OK',
    shortBanner: 'Not found at picking:',
    shortBannerHint: "found it? scan it — still missing? Can't find on its line",
    cantFindBtn: "Can't find",
    howManyMissing: 'How many are missing?',
    cantFindConfirm: "Can't find",
    hardToFind: 'Hard to find',
    missingMark: n => `missing ${n}`,
    undo: 'Undo',
    markRestHint: "Scan the rest, or press Can't find on them too — then the order goes to Can't find.",
    parcelSteps: ['Put the delivery note in the parcel', 'Close the parcel', 'Stick the label on it', 'Scan the label'],
    noteTick: 'The delivery note is in the parcel',
    noteTickFirst: 'First press: the delivery note is in the parcel',
    labelGateResume: order => `${order} is already packed — scan its parcel label now to finish it`,
    fixProcessing: 'Processing…',
    fixProcessed: order => `${order} processed ✓`,
    fixNotProcessed: (order, why) => `${order} NOT processed: ${why}. Take the parcel off the cart.`,
    fixNewNote: 'Put the NEW delivery note in the bag and throw the old one away',
    noShelf: 'no shelf',
  },
  et: {
    missingTitle: (o, t) => `Toode puudub — ${o} liigutatud: ${t}`,
    missingSteps: [
      'Pane kõik selle tellimuse tooted kotti',
      'Jäta kott lahti',
      'Kirjuta tellimuse number kotile',
      'Pane saateleht ja pakisilt kotti',
      'Pane lahtine kott halli kasti',
    ],
    noLabelTitle: 'Kõik tooted kontrollitud ✓ — pakisilti veel ei ole',
    noLabelSteps: [
      'Sulge kott',
      'Kirjuta tellimuse number markeriga kotile',
    ],
    noLabelMoved: o => `${o} liigutatud Label issue alla — skaneeri järgmine saateleht`,
    tooBigBtn: 'Kotti ei mahu — vaja juhatajat',
    tooBigTitle: o => `${o} ei mahu kotti`,
    tooBigPick: 'Võta tühi hall kast, millele on kirjutatud Too big 1, 2 või 3 — vajuta selle numbrit:',
    tooBigSteps: [
      'Pane kõik tooted, saateleht ja silt kasti',
      'Pane hall kast Too big riiulile',
    ],
    tooBigMoved: (o, b) => `${o} on hallis kastis Too big ${b} ja liigutatud Too Big alla — juhataja võtab sealt edasi`,
    tooBigMoveFailed: 'Tellimust ei saanud liigutada — vajuta kasti numbrit uuesti',
    noLabelMoveFailed: 'Tellimust ei õnnestunud Label issue alla liigutada — proovi uuesti või liiguta see Orders lehelt',
    retry: 'Proovi uuesti',
    done: 'Valmis — järgmine tellimus',
    cartLabel: 'Käru',
    cartLine: c => `Pane pakk ${c} kärusse`,
    scanNext: 'Skaneeri järgmine saateleht',
    boxCheck: 'Kontrollisin kastid: ühtegi toodet ega saatelehte ei jäänud üle',
    boxCheckHint: 'Vaata enne kinnitamist igasse kasti ja üle pakkimislaua — unustatud toode tähendab kliendi kaebust.',
    roundHeldTitle: 'Selle ringi tellimusi ei saa veel töödelda',
    roundHeldBody: 'Ringi kõik tellimused ei ole veel pakitud — saateleht, tooted, uuesti saateleht, pakisilt. Paki kõigepealt kogu ring lõpuni.',
    cancel: 'Tühista',
    inventoryCounting: 'Siin käib inventuur — jäta riiulile, inventuur toob selle',
    inventoryCountingLong: 'Seda riiulit loetakse praegu. Ära võta toodet; inventuuri tegija toob selle pakkijale.',
    nosWaiting: (n, of) => n < of ? `Ootab NOS-tarnet · ${n}/${of}` : 'Ootab NOS-tarnet',
    nosWaitingLong: (n, of) => `${n}/${of} tk ootavad tarnija saadetist (NOS) — neid ei saa korjata enne, kui see saabub.`,
    process: 'Töötle',
    ok: 'OK',
    roundWord: 'Ring',
    stillToPack: 'Veel pakkida',
    labelNotScanned: 'Silt skaneerimata',
    labelNotScannedHint: 'Skaneeri selle tellimuse saateleht pakkimisseansis uuesti ja vii lõpuni — koos sildiga.',
    notProcessedNow: 'Praegu töötlemata',
    processedOutside: n => `${n} tellimus${n === 1 ? '' : 't'} väljaspool seda ringi töödeldi.`,
    waitHint: 'Pakkisid just viimase? Oota hetk ja vajuta uuesti Töötle — skaneeringud jõuavad kohale mõne sekundiga.',
    forceHint: 'Juht / admin: töötle need tellimused siiski. Ütle, miks — see kirjutatakse igale tellimusele.',
    forceButton: 'Töötle siiski',
    colShelf: 'Riiul',
    colQty: 'Kogus',
    colItem: 'Toode (laos)',
    pcs: 'tk',
    inStock: n => `laos ${n}`,
    colLocation: 'Asukoht',
    colProduct: 'Toode',
    colScanned: 'Skaneeritud',
    packBtn: 'Paki',
    noteRow: 'Saateleht',
    noteRowHint: 'skaneeri see viimasena',
    noteScanNow: 'Skaneeri nüüd',
    allScannedNote: 'Kõik tooted skaneeritud ✓ — nüüd skaneeri SAATELEHT',
    multiTitle: 'Rohkem kui üks tükk',
    multiBody: 'Selles tellimuses on sama toodet rohkem kui üks tükk. Skaneeri IGA tükk eraldi — iga tükk peab piiksuma.',
    multiOk: 'Sain aru — skaneerin iga tüki',
    specialsTitle: p => `${andList(p, 'ja')} on siinsamas jaama juures`,
    placeA: 'Ä: riiul selle jaama kõrval',
    placeO: 'Ö: see laud (sokid)',
    specialsBody: 'Võta see kohe, pakkimise ajal — käruga neid keegi ei korja.',
    specialsOk: 'Selge',
    dontRemind: 'Ära rohkem meenuta',
    atStation: 'jaama juures',
    shortTitle: 'Korjaja ei leidnud seda',
    shortBody: "Kui see on nüüd sinu käes, skaneeri tavapäraselt. Kui see on ikka puudu, vajuta selle real Ei leia: tellimus läheb siis Can't find alla koos saatelehe, toodete ja sildiga.",
    shortOk: 'Selge',
    shortBanner: 'Korjamisel ei leitud:',
    shortBannerHint: 'leidsid? skaneeri — ikka puudu? Ei leia selle real',
    cantFindBtn: 'Ei leia',
    howManyMissing: 'Mitu on puudu?',
    cantFindConfirm: 'Ei leia',
    hardToFind: 'Raske leida',
    missingMark: n => `puudu ${n}`,
    undo: 'Tühista',
    markRestHint: "Skaneeri ülejäänud või vajuta ka nende real Ei leia — siis läheb tellimus Can't find alla.",
    parcelSteps: ['Pane saateleht pakki', 'Sulge pakk', 'Kleebi silt peale', 'Skaneeri silt'],
    noteTick: 'Saateleht on pakis',
    noteTickFirst: 'Kõigepealt vajuta: saateleht on pakis',
    labelGateResume: order => `${order} on juba pakitud — skanni nüüd selle paki silt, et see lõpetada`,
    fixProcessing: 'Töötlen…',
    fixProcessed: order => `${order} töödeldud ✓`,
    fixNotProcessed: (order, why) => `${order} EI OLE töödeldud: ${why}. Võta pakk kärult maha.`,
    fixNewNote: 'Pane kotti UUS saateleht ja viska vana ära',
    noShelf: 'riiul puudub',
  },
  ru: {
    missingTitle: (o, t) => `Товар отсутствует — ${o} перемещён: ${t}`,
    missingSteps: [
      'Положите все товары этого заказа в пакет',
      'Оставьте пакет открытым',
      'Напишите номер заказа на пакете',
      'Положите накладную и этикетку в пакет',
      'Положите открытый пакет в серый ящик',
    ],
    noLabelTitle: 'Все товары проверены ✓ — этикетки пока нет',
    noLabelSteps: [
      'Запечатайте пакет',
      'Напишите номер заказа на пакете перманентным маркером',
    ],
    noLabelMoved: o => `${o} перемещён в Label issue — отсканируйте следующую накладную`,
    tooBigBtn: 'Не помещается в пакет — нужен менеджер',
    tooBigTitle: o => `${o} не помещается в пакет`,
    tooBigPick: 'Возьмите пустой серый ящик с надписью Too big 1, 2 или 3 — нажмите его номер:',
    tooBigSteps: [
      'Положите в ящик все товары, накладную и этикетку',
      'Поставьте серый ящик на полку Too big',
    ],
    tooBigMoved: (o, b) => `${o} в сером ящике Too big ${b} и перемещён в Too Big — дальше им займётся менеджер`,
    tooBigMoveFailed: 'Не удалось переместить заказ — нажмите номер ящика ещё раз',
    noLabelMoveFailed: 'Не удалось переместить заказ в Label issue — попробуйте ещё раз или переместите его со страницы Orders',
    retry: 'Попробовать ещё раз',
    done: 'Готово — следующий заказ',
    cartLabel: 'Тележка',
    cartLine: c => `Положите посылку в тележку ${c}`,
    scanNext: 'Отсканируйте следующую накладную',
    boxCheck: 'Я проверил(а) ящики: не осталось ни одного товара и ни одной накладной',
    boxCheckHint: 'Перед подтверждением загляните в каждый ящик и осмотрите упаковочный стол — забытый товар означает жалобу клиента.',
    roundHeldTitle: 'Заказы этого раунда пока нельзя обработать',
    roundHeldBody: 'Не все заказы раунда упакованы — накладная, товары, снова накладная, этикетка. Сначала допакуйте весь раунд.',
    cancel: 'Отмена',
    inventoryCounting: 'Здесь идёт инвентаризация — оставьте, принесут',
    inventoryCountingLong: 'Эта полка сейчас пересчитывается. Не берите товар; сотрудник инвентаризации принесёт его упаковщику.',
    nosWaiting: (n, of) => n < of ? `Ждёт поставку NOS · ${n} из ${of}` : 'Ждёт поставку NOS',
    nosWaitingLong: (n, of) => `${n} из ${of} шт. ждут поставку от поставщика (NOS) — их нельзя собрать, пока она не придёт.`,
    process: 'Обработать',
    ok: 'OK',
    roundWord: 'Раунд',
    stillToPack: 'Ещё не упаковано',
    labelNotScanned: 'Этикетка не отсканирована',
    labelNotScannedHint: 'Отсканируйте накладную этого заказа ещё раз в сеансе упаковки и доведите до конца — вместе с этикеткой.',
    notProcessedNow: 'Сейчас не обработано',
    processedOutside: n => `Обработано заказов вне этого раунда: ${n}.`,
    waitHint: 'Только что упаковали последний? Подождите немного и нажмите «Обработать» ещё раз — сканы приходят за несколько секунд.',
    forceHint: 'Менеджер / админ: обработать эти заказы всё равно. Укажите причину — она будет записана на каждом заказе.',
    forceButton: 'Обработать всё равно',
    colShelf: 'Полка',
    colQty: 'Кол-во',
    colItem: 'Товар (на складе)',
    pcs: 'шт',
    inStock: n => `на складе ${n}`,
    colLocation: 'Место',
    colProduct: 'Товар',
    colScanned: 'Отсканировано',
    packBtn: 'Упаковать',
    noteRow: 'Накладная',
    noteRowHint: 'отсканируйте последней',
    noteScanNow: 'Сканируйте сейчас',
    allScannedNote: 'Все товары отсканированы ✓ — теперь отсканируйте НАКЛАДНУЮ',
    multiTitle: 'Больше одной штуки',
    multiBody: 'В этом заказе больше одной штуки одного товара. Сканируйте КАЖДУЮ штуку отдельно — каждая должна пикнуть.',
    multiOk: 'Понятно — сканирую каждую штуку',
    specialsTitle: p => `${andList(p, 'и')} — прямо здесь, у станции`,
    placeA: 'Ä: полка рядом с этой станцией',
    placeO: 'Ö: этот стол (носки)',
    specialsBody: 'Возьмите товар сейчас, во время упаковки — с тележкой их никто не собирает.',
    specialsOk: 'Понятно',
    dontRemind: 'Больше не напоминать',
    atStation: 'у станции',
    shortTitle: 'Сборщик не нашёл это',
    shortBody: "Если товар теперь у вас — сканируйте как обычно. Если его всё ещё нет, нажмите «Не нахожу» в его строке: заказ уйдёт в Can't find вместе с накладной, товарами и этикеткой.",
    shortOk: 'Понятно',
    shortBanner: 'Не найдено при сборе:',
    shortBannerHint: 'нашли? сканируйте — всё ещё нет? «Не нахожу» в строке',
    cantFindBtn: 'Не нахожу',
    howManyMissing: 'Сколько не хватает?',
    cantFindConfirm: 'Не нахожу',
    hardToFind: 'Трудно найти',
    missingMark: n => `не хватает ${n}`,
    undo: 'Отменить',
    markRestHint: "Отсканируйте остальное или нажмите «Не нахожу» и в их строках — тогда заказ уйдёт в Can't find.",
    parcelSteps: ['Положите накладную в посылку', 'Закройте посылку', 'Наклейте этикетку', 'Отсканируйте этикетку'],
    noteTick: 'Накладная в посылке',
    noteTickFirst: 'Сначала нажмите: накладная в посылке',
    labelGateResume: order => `${order} уже упакован — отсканируйте этикетку его посылки, чтобы завершить`,
    fixProcessing: 'Обработка…',
    fixProcessed: order => `${order} обработан ✓`,
    fixNotProcessed: (order, why) => `${order} НЕ обработан: ${why}. Снимите посылку с тележки.`,
    fixNewNote: 'Положите в пакет НОВУЮ накладную, а старую выбросьте',
    noShelf: 'нет полки',
  },
};

export function packingText(lang: string | null | undefined): Texts {
  return T[(lang as Lang) in T ? (lang as Lang) : 'en'];
}

/** Which cart a parcel goes into: the first rule whose carrier matches and whose countries
 *  (empty = any) include the destination. No rule → the carrier's plain name. */
export interface CartRule { carrier: string; countries: string[]; cart: string }
export function cartFor(rules: CartRule[], carrier: string | null | undefined, country: string | null | undefined): string {
  const c = (carrier || '').toLowerCase();
  const cc = (country || '').toUpperCase();
  const hit = rules.find(r => r.carrier === c && (!r.countries?.length || r.countries.includes(cc)));
  if (hit) return hit.cart;
  return CARRIER_NAME[c] || carrier || '?';
}
